import React, { useEffect, useState } from 'react';
import { defer } from '/imports/utils';
import { InputProps } from '/imports/ui/Input';
import { Icon } from '/imports/ui/Icon';

// Base classes mirror the (now Tailwind) Input component; the spin-button
// utilities are the only NumberInput-specific additions, reproducing the old
// styled(InputStyled) rules that hid the number spinners.
const numberInputWrapperClass = 'input relative [&.error_input]:border-red-2 [&_input::-webkit-outer-spin-button]:appearance-none [&_input::-webkit-outer-spin-button]:m-0 [&_input::-webkit-inner-spin-button]:appearance-none [&_input::-webkit-inner-spin-button]:m-0 [&_input[type=number]]:[appearance:inherit] [&_input[type=number]]:[-moz-appearance:textfield]';
const numberInputElementClass = 'box-border min-h-input rounded-input border border-mid bg-page py-0 pr-10 pl-3 font-sans text-[13px] text-fg-1 placeholder:italic focus:[outline:1px_solid_var(--color-primary)] disabled:cursor-not-allowed disabled:bg-grey-8';

export interface NumberInputProps extends InputProps<number> {
  className?: string;
  placeholder?: string;
  int?: boolean;
  disabled?: boolean;
  step?: string;
  min?: string;
  max?: string;
  unitPrefix?: string;
  onFocus?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  onBlur?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  defaultStrValue?: string;
}

export const NumberInput = (props: NumberInputProps) => {
  const defaultStrValue = props.defaultStrValue || '';
  const [strValue, setStrValue] = useState(props.value !== undefined ? `${props.value}` : defaultStrValue);
  const [isValid, setIsValid] = useState(true);
  useEffect(() => {
    setStrValue(props.value !== undefined ? `${props.value}` : defaultStrValue);
  }, [props.value]);
  const hasStartIcon = props.icon && props.iconPlacement !== 'end';
  const clearInput = () => {
    props.onChange?.(undefined);
    defer(() => props.onClear?.());
    defer(() => setStrValue(defaultStrValue));
  };
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = props.int ? parseInt(e.target.value) : parseFloat(e.target.value);
    const isNum = !isNaN(value);
    if (isNum && props.min) value = Math.max(parseFloat(props.min), value);
    if (isNum && props.max) value = Math.min(parseFloat(props.max), value);
    setStrValue(`${e.target.value}`);
    isNum && props.onChange?.(value, e);
    setIsValid(isNum);
  };
  const onBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    if (isValid) return props.onBlur?.(e);
    setStrValue(`${props.value}`);
    props.onChange?.(props.value);
    props.onBlur?.(e);
  };
  return (
    <span id={props.id} className={`number-input ${numberInputWrapperClass} ${props.className || ''}${props.hasError ? ' error' : ''}`}>
      {hasStartIcon && <Icon className="icon-start absolute top-4 left-4" icon={props.icon as any} />}
      <input
        id={props.inputId}
        className={`${numberInputElementClass}${hasStartIcon ? ' has-start-icon pl-10' : ''}`}
        type="number"
        step={props.step || ''}
        min={props.min}
        max={props.max}
        placeholder={props.placeholder || ''}
        disabled={props.disabled}
        autoFocus={props.autoFocus}
        value={strValue}
        onChange={(e) => onChange(e)}
        onClick={(e) => props.onClick?.(e)}
        onFocus={props.onFocus}
        onBlur={(e) => onBlur(e)}
        onKeyUp={(e) => {
          props.onKeyPress?.(e);
          if (e.key === 'Enter') defer(() => props.onEnter?.(props.value, e));
        }}
        autoComplete={props.autoComplete}
        required={props.required}
      />
      {
        props.value !== undefined &&
        !props.disabled &&
        !(props.type === 'color') &&
        <Icon className="clear absolute top-0 right-5 inline-flex h-full w-4 cursor-pointer" icon="cancel" onClick={() => clearInput()} /> || null
      }
    </span>
  );
};